import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { GrowthbookFeatures } from "components/growthbook/growthbook.types";
import { SubscriptionType } from "services/api/sessions/sessionService.types";
import { useAuth } from "stores/auth/auth-context";
import { useKelasLiveContext } from "stores/kelas-live/kelas-live-context";
import {
  ISlotSelectionEligibility,
  IUseEligibleSlotSelection
} from "./use-eligible-slot-selection.types";

const useEligibleSlotSelection = (): IUseEligibleSlotSelection => {
  const isEligibleToSkipFeatureOn = useFeatureIsOn(
    GrowthbookFeatures.SlotSelectionSkipButton as string
  );
  const {
    user: { isUserLoggedIn }
  } = useAuth();

  const { userSubscription } = useKelasLiveContext();
  const { subscriptionStatus, isFetching } = userSubscription || {};
  const subscriptionType = subscriptionStatus?.subscriptionType;
  const pendingSlotSelection = subscriptionStatus?.pendingSlotSelection;

  // NOTE: eligibility is derived synchronously during render (instead of being
  // copied into state via useEffect). This keeps it in sync with
  // `subscriptionType` within the same render. Previously the one-render lag
  // caused the /bimbel redirect to read a fresh `subscriptionType` together
  // with a stale (empty) eligibility, wrongly routing eligible paid users to
  // /bimbel/user instead of /bimbel/slot-selection.
  const isEligible = Boolean(
    isUserLoggedIn &&
      subscriptionType === SubscriptionType.PAID &&
      pendingSlotSelection?.pendingCourseIds?.length
  );

  // Subscription data is considered resolved once we are not fetching and
  // either the user is logged out (no subscription expected) or the
  // subscription status has already loaded. While unresolved we expose
  // `eligibility = null` so consumers keep waiting instead of acting on
  // incomplete data.
  const isResolved =
    !isFetching && (!isUserLoggedIn || Boolean(subscriptionStatus));

  let eligibility: ISlotSelectionEligibility | null = null;
  if (isResolved) {
    eligibility =
      isEligible && pendingSlotSelection
        ? {
            courseIds: pendingSlotSelection.pendingCourseIds,
            userSubscriptionId: pendingSlotSelection.userSubscriptionId
          }
        : { courseIds: [], userSubscriptionId: "" };
  }

  const isEligibleToSkip = Boolean(
    subscriptionStatus?.subscribedCourses &&
      subscriptionStatus.subscribedCourses.length > 0
  );

  return {
    isEligibleSlotSelection: isEligible,
    eligibility,
    isEligibleToSkipSlotSelection:
      isEligibleToSkip && isEligibleToSkipFeatureOn,
    isLoading: !isResolved
  };
};

export default useEligibleSlotSelection;
